import posthog from "posthog-js"
import type { BeforeSendFn, CaptureResult } from "posthog-js"

import { IS_PROD } from "@/lib/constants"

const projectToken = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN

// Clicking into a text field is a "dead click" by PostHog's definition — the
// caret moves and nothing else on the page changes — so these fire constantly
// and drown out the real signal. A click on a *disabled* field is genuinely
// dead in the way we care about, so those are kept.
const FORM_FIELD_TAGS = new Set(["textarea", "input", "select"])

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const isDisabled = (element: Record<string, unknown>) => {
  // Autocapture serializes DOM attributes as `attr__<name>`. A bare `disabled`
  // attribute comes through as the empty string, so presence is what counts.
  if (!("attr__disabled" in element)) {
    return false
  }
  const disabled = element["attr__disabled"]
  return disabled != null && disabled !== "false"
}

// The element chain runs from the clicked element outward to its ancestors.
const clickedElement = (event: CaptureResult): Record<string, unknown> | null => {
  const properties: unknown = event.properties
  if (!isRecord(properties) || !("$elements" in properties)) {
    return null
  }
  const elements = properties["$elements"]
  if (!Array.isArray(elements)) {
    return null
  }
  const clicked: unknown = elements[0]
  return isRecord(clicked) ? clicked : null
}

const dropDeadClicksOnEnabledFormFields: BeforeSendFn = (event) => {
  if (event?.event !== "$dead_click") {
    return event
  }
  const clicked = clickedElement(event)
  if (clicked === null || !("tag_name" in clicked)) {
    return event
  }
  const tagName = clicked["tag_name"]
  if (typeof tagName !== "string") {
    return event
  }
  if (FORM_FIELD_TAGS.has(tagName.toLowerCase()) && !isDisabled(clicked)) {
    return null
  }
  return event
}

// Only run analytics in production, matching the Sentry client config. Staging
// and local dev share this bundle, so the check is on the serving origin.
if (IS_PROD && projectToken) {
  posthog.init(projectToken, {
    // Managed reverse proxy on our own domain, so ingestion isn't blocked by
    // ad/tracker blockers that match posthog.com.
    api_host: "https://t.lumen.energy",
    // The proxy only fronts ingestion. Links out to the PostHog app (toolbar,
    // session replay) have to target the real UI host or they 404.
    ui_host: "https://us.posthog.com",
    defaults: "2026-05-30",
    // Already the SDK default; stated explicitly because it decides whether
    // anonymous visitors get person profiles, which is what we're billed on.
    person_profiles: "identified_only",
    capture_exceptions: true,
    before_send: dropDeadClicksOnEnabledFormFields,
  })
}

export { posthog, dropDeadClicksOnEnabledFormFields }
